import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import SelectPaymentMethod from './SelectPaymentMethod';
import ShippingConfirm from './ShippingConfirm';
import CardPayment from './PaymentMethods/CardPayment';
import DealerTermsPayment from './PaymentMethods/DealerTermsPayment';
import { getQuoteColor } from '../QuoteObjectExtensions';
import RMAPaymentOverride from './PaymentMethods/RMAPaymentOverride';
import { useQuotePricing } from '../../Store/entities/quotes/hooks';
import apiCalls from '../../PowerShadesAPIFunctions';
import { QuoteStore } from '../../Store/entities/entitiesType';
import { fakeRootType } from '../../FakeRootType';
import Button from '../../Parts/Button';

type PaymentStepsProps = {
	quoteId: number;
	setProcessing: (processing: boolean) => void;
	processing: boolean;
	showingSubmit: boolean;
	onSubmit: () => void;
	quote: QuoteStore;
	hasCard: boolean;
	hasTerms: boolean;
	setAdminPayment: (adminPayment: boolean) => void;
	ROOT: fakeRootType;
	quoteType: string;
	setSubmit: (new_submit: any) => any;
	setShowingSubmit: (showing: boolean) => void;
};

const PaymentSteps = ({
	quoteId,
	setProcessing,
	processing,
	showingSubmit,
	onSubmit,
	quote,
	hasCard,
	hasTerms,
	setAdminPayment,
	ROOT,
	quoteType,
	setSubmit,
	setShowingSubmit,
}: PaymentStepsProps) => {
	const [paymentMethod, setPaymentMethod] = useState(null);
	const [currentStep, setCurrentStep] = useState(0);
	const [hideNext, setHideNext] = useState(false);
	const [actuallyHideNext, setActuallyHideNext] = useState(false);
	const [, setError] = useState(false);
	const [invalidShippingAddress, setInvalidShippingAddress] = useState(false);
	const [, setInvalidInstallationAddress] = useState(false);

	const [willNeedRetainer, setWillNeedRetainer] = useState(false);

	const quotePricing = useQuotePricing(quoteId);

	const setRetainer = (paymentType) => {
		const retainerCheck
		= paymentType === 'card'
			? apiCalls.cardRetainerNeeded
			: paymentType === 'DT'
				? apiCalls.termsRetainerNeeded
				: paymentType === 'ach' ? apiCalls.achRetainerNeeded
					: null;

		retainerCheck ? retainerCheck(quoteId).then((resp) => {
			setWillNeedRetainer(resp.data.is_retainer_required);
		}).catch((err) => {
			console.error(err);
			throw new Error('Something went wrong checking if this order needs a retainer.'
				+ ' Please check console for resp details.');
		})
			: console.log('Could not determine retainer check for payment type: ', paymentType);
	};
	
	const moveToFinal = (paymentMethodInput?: string) => {
		const finalSteps = {
			card: 2,
			dealer_terms: 3,
			rma_override: 4
		};

		const localPaymentMethods = paymentMethodInput ?? paymentMethod;

		localPaymentMethods
			? setCurrentStep(finalSteps[localPaymentMethods])
			: setError(true);

		setHideNext(true);
		setActuallyHideNext(true);
	};

	const halfPrice = ((quotePricing?.total_price || 0) / 2).toFixed(2);
	const quoteColor = getQuoteColor(quoteType);

	const steps = {
		0: (
			<ShippingConfirm
				quoteType={quoteType}
				quote={quote}
				setInvalidShippingAddress={setInvalidShippingAddress}
				setInvalidInstallationAddress={setInvalidInstallationAddress}
				hideNext={() => setHideNext(true)}
				showNext={() => setHideNext(false)}
				ROOT={ROOT}
			/>
		),

		1: (
			<SelectPaymentMethod
				quoteColor={quoteColor}
				quoteType={quoteType}
				hasCard={hasCard}
				hasTerms={hasTerms}
				quote={quote}
				hideNext={() => setHideNext(true)}
				showNext={() => setHideNext(false)}
				setAdminPayment={setAdminPayment}
				paymentMethodSelected={(paymentMethodInput) => {
					moveToFinal(paymentMethodInput);
				}}
			/>
		),

		2: (
			<CardPayment
				quoteId={quoteId}
				quoteType={quoteType}
				halfPrice={halfPrice}
				setProcessing={setProcessing}
				quote={quote}
				setSubmit={setSubmit}
				showSubmit={() => setShowingSubmit(true)}
			/>
		),

		3: (
			<DealerTermsPayment
				halfPrice={halfPrice}
				willNeedRetainer={willNeedRetainer}
				setProcessing={setProcessing}
				quote={quote}
				setSubmit={setSubmit}
				showSubmit={() => setShowingSubmit(true)}
			/>
		),

		4: (
			<RMAPaymentOverride
				quoteId={quote.id}
			
			/>

		)
	};

	const backStep = () => {
		setShowingSubmit(false);

		if (currentStep === 1) {
			setActuallyHideNext(false);
			setPaymentMethod(null);
			setCurrentStep(0);
		}

		if (currentStep === 2 || currentStep === 3 || currentStep === 4) {
			setCurrentStep(1);
		}
	};

	const next = () => {
		if (currentStep === 0) {
			apiCalls.shippingNextPressed(quoteId).then(() => {
				console.log('shippingNextPressed');
			});

			setCurrentStep(1);
			setActuallyHideNext(true);
		} else {
			moveToFinal();
		}
	};

	return currentStep === 4 ? steps[currentStep]
		: (
			<>
				{steps[currentStep]}

				{currentStep > -1 ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
						}}
					>
						{currentStep > 0 ? (
							<button
								type="button"
								onClick={backStep}
								style={{
									background: 'none',
									marginRight: 'auto',
									width: '6em',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: quoteColor,
								}}
							>
								<FontAwesomeIcon icon={faArrowLeftLong} />
								<span
									style={{
										fontSize: '20px',
										fontWeight: 'normal',
										textAlign: 'left',
										paddingLeft: '5px',
									}}
								>
									Back
								</span>
							</button>
						) : null}
						{showingSubmit ? (
							<button
								type="button"
								disabled={processing}
								onClick={onSubmit}
								style={{
									background: 'none',
									marginLeft: 'auto',
									width: '6em',
									alignItems: 'center',
									justifyContent: 'center',
									color: quoteColor,
									display: 'flex',
									opacity: processing ? 0.6 : 1,
									cursor: processing ? 'not-allowed' : 'pointer',
								}}
							>
								{
									processing
										? <span
											style={{
												fontSize: '20px',
												fontWeight: 'normal',
												textAlign: 'right',
											}}
										>
											Processing...
										</span>
										: <Button color={quoteType}>Submit</Button>
								}
							</button>
						) : (
							<button
								type="button"
								onClick={() => {
									hideNext
										? ROOT.alert({ quickFormat: 'error',
											text: `Please enter the ${
												invalidShippingAddress
													? 'Property / Company Name'
													: 'Property Name'
											} for ${
												invalidShippingAddress
													? 'Shipping Address'
													: 'Installation Address'
											}` })
										: next();
									!hideNext && setError(true);
								}}
								style={{
									background: 'none',
									marginLeft: 'auto',
									width: '6em',
									alignItems: 'center',
									justifyContent: 'center',
									color: quoteColor,
									display: actuallyHideNext ? 'none' : 'flex',
								}}
							>
								<span
									style={{
										fontSize: '20px',
										fontWeight: 'normal',
										textAlign: 'right',
										paddingRight: '5px',
									}}
								>
									Next
								</span>
								<FontAwesomeIcon icon={faArrowRightLong} />
							</button>
						)}
					</div>
				) : null}
			</>);
};

export default PaymentSteps;
